/**
 * ChecklistFilterBar — Filter bar with animated filter chips,
 * expandable search, and advanced filter popover.
 *
 * Uses FilterChips and Filter.Trigger/Popover from @/components/ui.
 */

import { useState, useMemo } from "react";
import { motion } from "framer-motion";
import { Search, FileText, AlertTriangle } from "lucide-react";
import { springs, FilterChips, Filter } from "@/components/ui";
import type { FilterChipData } from "@/components/ui";
import type { UIChecklistItem, ChecklistFilterType } from "@/packages/types";
import { useTranslation } from "@/packages/utils";

// ─── Filter Options ─────────────────────────────────────────────────────────

const FILTER_KEYS: Record<ChecklistFilterType, string> = {
  all: "checklist.filters.all",
  open: "checklist.filters.open",
  completed: "checklist.filters.completed",
};

const FILTER_VALUES: ChecklistFilterType[] = ["all", "open", "completed"];

function countForFilter(
  items: UIChecklistItem[],
  filterType: ChecklistFilterType,
): number {
  switch (filterType) {
    case "all":
      return items.length;
    case "open":
      return items.filter((item) => !item.completedAt).length;
    case "completed":
      return items.filter((item) => !!item.completedAt).length;
  }
}

// ─── Component ──────────────────────────────────────────────────────────────

interface ChecklistFilterBarProps {
  items: UIChecklistItem[];
  activeFilter: ChecklistFilterType;
  onFilterChange: (filter: ChecklistFilterType) => void;
  searchQuery: string;
  onSearchChange: (query: string) => void;
  hasEvidenceFilter: boolean;
  onHasEvidenceChange: (v: boolean) => void;
  hasFindingsFilter: boolean;
  onHasFindingsChange: (v: boolean) => void;
  hasEvidenceCount: number;
  hasFindingsCount: number;
}

export function ChecklistFilterBar({
  items,
  activeFilter,
  onFilterChange,
  searchQuery,
  onSearchChange,
  hasEvidenceFilter,
  onHasEvidenceChange,
  hasFindingsFilter,
  onHasFindingsChange,
  hasEvidenceCount,
  hasFindingsCount,
}: ChecklistFilterBarProps) {
  const { t } = useTranslation("audits");
  const [searchFocused, setSearchFocused] = useState(false);

  const chips = useMemo<FilterChipData<ChecklistFilterType>[]>(() => {
    return FILTER_VALUES.map((value) => ({
      id: value,
      label: t(FILTER_KEYS[value] as "checklist.filters.all") as string,
      count: countForFilter(items, value),
    }));
  }, [items, t]);

  return (
    <motion.div
      className="flex items-center gap-1.5 mb-3"
      initial={{ opacity: 0, y: 4 }}
      animate={{ opacity: 1, y: 0 }}
      transition={springs.snappy}
    >
      <FilterChips
        chips={chips}
        activeFilter={activeFilter}
        onFilterChange={onFilterChange}
        layoutId="checklist-filter-pill"
        className="flex items-center gap-1.5"
      />

      <div className="h-4 w-px bg-border/50 mx-1" />

      <button
        type="button"
        onClick={() => onHasEvidenceChange(!hasEvidenceFilter)}
        className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors cursor-pointer whitespace-nowrap border ${
          hasEvidenceFilter
            ? "border-accent bg-accent/10 text-accent font-semibold"
            : "border-border bg-transparent text-secondary hover:border-accent/50"
        }`}
      >
        <FileText size={11} />
        <span>
          {t("checklist.filters.hasEvidence" as "checklist.filters.all")}
        </span>
        {hasEvidenceFilter && (
          <span className="text-[11px] tabular-nums opacity-70">
            {hasEvidenceCount}
          </span>
        )}
      </button>

      <button
        type="button"
        onClick={() => onHasFindingsChange(!hasFindingsFilter)}
        className={`flex items-center gap-1.5 rounded-full px-3 py-1.5 text-[12px] font-medium transition-colors cursor-pointer whitespace-nowrap border ${
          hasFindingsFilter
            ? "border-accent bg-accent/10 text-accent font-semibold"
            : "border-border bg-transparent text-secondary hover:border-accent/50"
        }`}
      >
        <AlertTriangle size={11} />
        <span>
          {t("checklist.filters.hasFindings" as "checklist.filters.all")}
        </span>
        {hasFindingsFilter && (
          <span className="text-[11px] tabular-nums opacity-70">
            {hasFindingsCount}
          </span>
        )}
      </button>

      {/* Spacer */}
      <div className="flex-1" />

      {/* Expandable search */}
      <motion.div
        className="flex items-center rounded-full border bg-surface overflow-hidden"
        animate={{
          borderColor: searchFocused ? "#E8734A" : "var(--color-border)",
          boxShadow: searchFocused
            ? "0 0 0 2px rgba(232,115,74,0.1)"
            : "0 0 0 0px rgba(232,115,74,0)",
          width: searchFocused || searchQuery ? 200 : 32,
        }}
        transition={springs.snappy}
      >
        <Search
          className="mx-2 h-3.5 w-3.5 shrink-0 text-muted cursor-pointer"
          strokeWidth={1.8}
          onClick={() => setSearchFocused(true)}
        />
        <input
          type="text"
          placeholder={t("checklist.searchPlaceholder")}
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          onFocus={() => setSearchFocused(true)}
          onBlur={() => setSearchFocused(false)}
          className="h-7 w-full bg-transparent pr-2 text-[12px] text-primary placeholder:text-muted outline-none"
        />
      </motion.div>

      <Filter.Popover />
    </motion.div>
  );
}
